/**
 * Generate a thumbnail from an image or video File using the Canvas API.
 * Returns a base64 data URI (JPEG, quality 0.7) or null if generation fails.
 *
 * - Images: draws directly to canvas
 * - Videos: seeks to 1s (or 0s) and captures a frame
 * - Max dimension: 200x200, preserving aspect ratio
 */

const MAX_SIZE = 200
const JPEG_QUALITY = 0.7

/**
 * Generate a JPEG thumbnail data URI from a File.
 * Returns null if the file type is unsupported or an error occurs.
 */
export async function generateThumbnail(file: File): Promise<string | null> {
  try {
    if (file.type.startsWith('image/')) {
      return await thumbnailFromImage(file)
    }
    if (file.type.startsWith('video/')) {
      return await thumbnailFromVideo(file)
    }
    return null
  } catch (err) {
    console.warn('[thumbnail] Generation failed:', err)
    return null
  }
}

function thumbnailFromImage(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      const dataUri = drawToCanvas(img, img.naturalWidth, img.naturalHeight)
      URL.revokeObjectURL(url)
      resolve(dataUri)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }

    img.src = url
  })
}

function thumbnailFromVideo(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)

    // Timeout after 10s — some formats won't load
    const timeout = setTimeout(() => {
      cleanup()
      resolve(null)
    }, 10_000)

    function cleanup() {
      clearTimeout(timeout)
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(url)
    }

    video.onloadeddata = () => {
      // Seek to 1 second for a more representative frame
      const seekTarget = Math.min(1, video.duration || 0)
      video.currentTime = seekTarget
    }

    video.onseeked = () => {
      const dataUri = drawToCanvas(video, video.videoWidth, video.videoHeight)
      cleanup()
      resolve(dataUri)
    }

    video.onerror = () => {
      cleanup()
      resolve(null)
    }

    video.preload = 'metadata'
    video.muted = true
    video.src = url
  })
}

function drawToCanvas(
  source: HTMLImageElement | HTMLVideoElement,
  srcWidth: number,
  srcHeight: number
): string | null {
  if (srcWidth === 0 || srcHeight === 0) return null

  // Calculate scaled dimensions preserving aspect ratio
  let w = srcWidth
  let h = srcHeight
  if (w > h) {
    if (w > MAX_SIZE) {
      h = Math.round(h * (MAX_SIZE / w))
      w = MAX_SIZE
    }
  } else {
    if (h > MAX_SIZE) {
      w = Math.round(w * (MAX_SIZE / h))
      h = MAX_SIZE
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.drawImage(source, 0, 0, w, h)
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY)
}
